import React, { Fragment, useState, useEffect } from 'react';
import { sacpmApi, usersApi, processesApi, sectorsApi, useAuth } from '../api';
import ElectronicSignature from '../components/ElectronicSignature';
import ColumnSelector from '../components/ColumnSelector';
import useColumnVisibility from '../hooks/useColumnVisibility';
import { toast } from 'sonner';
import { extractApiError, validateDateFields } from '../utils/dateValidation';
import {
  RefreshCw,
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  Loader2,
  CheckCircle,
  XCircle,
  Eye,
  FileText,
  AlertTriangle,
  TrendingUp,
  Shield,
  Upload,
  Download,
  PlusCircle,
  Printer,
  Minus,
} from 'lucide-react';

const SACPM_TYPES = [
  { value: 'corrective', label: 'Ação Corretiva', color: 'badge-red', icon: AlertTriangle },
  { value: 'preventive', label: 'Ação Preventiva', color: 'badge-blue', icon: Shield },
  { value: 'improvement', label: 'Melhoria', color: 'badge-green', icon: TrendingUp },
];

const ORIGINS = [
  { value: 'non_conformity', label: 'Não Conformidade' },
  { value: 'audit', label: 'Auditoria' },
  { value: 'risk', label: 'Análise de Risco' },
  { value: 'customer_feedback', label: 'Feedback de Cliente' },
  { value: 'process_analysis', label: 'Análise de Processo' },
  { value: 'management_review', label: 'Análise Crítica' },
  { value: 'suggestion', label: 'Sugestão' },
  { value: 'other', label: 'Outros' },
];

const PRIORITIES = [
  { value: 'low', label: 'Baixa', color: 'badge-gray' },
  { value: 'medium', label: 'Média', color: 'badge-blue' },
  { value: 'high', label: 'Alta', color: 'badge-yellow' },
  { value: 'critical', label: 'Crítica', color: 'badge-red' },
];

const ROOT_CAUSE_METHODS = [
  '5 Porquês',
  'Ishikawa (Espinha de Peixe)',
  'Diagrama de Pareto',
  'Brainstorming',
  'Análise de Processo',
  'Outro',
];

const ACTION_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pendente' },
  { value: 'in_progress', label: 'Em andamento' },
  { value: 'completed', label: 'Concluída' },
  { value: 'cancelled', label: 'Cancelada' },
];

const STATUS_CONFIG = {
  draft: { label: 'Rascunho', color: 'badge-gray', icon: RefreshCw },
  open: { label: 'Aberta', color: 'badge-yellow', icon: AlertTriangle },
  analyzing: { label: 'Em Análise de Causa', color: 'badge-blue', icon: RefreshCw },
  planning: { label: 'Planejando Ações', color: 'badge-blue', icon: FileText },
  executing: { label: 'Em Execução', color: 'badge-yellow', icon: RefreshCw },
  verifying: { label: 'Em Verificação', color: 'badge-blue', icon: Eye },
  closed_effective: { label: 'Encerrada - Eficaz', color: 'badge-green', icon: CheckCircle },
  closed_ineffective: { label: 'Encerrada - Não Eficaz', color: 'badge-red', icon: XCircle },
  reopened_ineffective: { label: 'Reaberta - Não Eficaz', color: 'badge-red', icon: RefreshCw },
  cancelled: { label: 'Cancelada', color: 'badge-gray', icon: XCircle },
};

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'];

const toISODate = (d) => (d ? new Date(d).toLocaleDateString('pt-BR') : '-');

const SACPM = () => {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [users, setUsers] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [actionPlan, setActionPlan] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [formData, setFormData] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);

  // Closure state
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closePhoto, setClosePhoto] = useState(null);
  const [closeEffectiveness, setCloseEffectiveness] = useState('effective');
  const [closeVerificationResult, setCloseVerificationResult] = useState('');
  const [closing, setClosing] = useState(false);
  const [showSignature, setShowSignature] = useState(false);

  // PDF
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    loadData();
    loadUsers();
    loadSectors();
    loadProcesses();
  }, [filterType, filterStatus]);

  function emptyForm() {
    return {
      title: '',
      description: '',
      sacpm_type: 'corrective',
      origin: 'other',
      priority: 'medium',
      area: '',
      process: '',
      problem_description: '',
      detection_location: '',
      external_reference: '',
      immediate_action: '',
      immediate_action_description: '',
      root_cause: '',
      root_cause_method: '',
      extends_to_other_products: '',
      extends_target_products_processes: '',
      target_date: '',
      responsible_id: '',
      verification_criteria: '',
      verification_result: '',
      is_effective: '',
    };
  }

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterType) params.sacpm_type = filterType;
      if (filterStatus) params.status = filterStatus;

      const [recordsRes, statsRes] = await Promise.all([
        sacpmApi.list(params),
        sacpmApi.stats(),
      ]);
      setRecords(recordsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadSectors = async () => {
    try {
      const response = await sectorsApi.listActive();
      setSectors(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar setores:', error);
    }
  };

  const loadProcesses = async () => {
    try {
      const response = await processesApi.listActive();
      setProcesses(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar processos:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await usersApi.list({ is_active: true });
      setUsers(res.data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadAttachments = async (sacpmId) => {
    try {
      const res = await sacpmApi.listAttachments(sacpmId);
      setAttachments(res.data || []);
    } catch (error) {
      setAttachments([]);
    }
  };

  const canEditRecord = (record) => {
    if (!user) return false;
    return user.is_admin || user.id === record?.requester_id;
  };

  const handleOpenModal = async (record = null) => {
    if (record) {
      try {
        const res = await sacpmApi.get(record.id);
        const full = res.data;
        setEditingRecord(full);
        setFormData({
          title: full.title || '',
          description: full.description || '',
          sacpm_type: full.sacpm_type || 'corrective',
          origin: full.origin || 'other',
          priority: full.priority || 'medium',
          area: full.area || '',
          process: full.process || '',
          problem_description: full.problem_description || '',
          detection_location: full.detection_location || '',
          external_reference: full.external_reference || '',
          immediate_action: full.immediate_action || '',
          immediate_action_description: full.immediate_action_description || '',
          root_cause: full.root_cause || '',
          root_cause_method: full.root_cause_method || '',
          extends_to_other_products: full.extends_to_other_products === null || full.extends_to_other_products === undefined
            ? ''
            : full.extends_to_other_products ? 'sim' : 'nao',
          extends_target_products_processes: full.extends_target_products_processes || '',
          target_date: full.target_date || '',
          responsible_id: full.responsible_id || '',
          verification_criteria: full.verification_criteria || '',
          verification_result: full.verification_result || '',
          is_effective: full.is_effective === null || full.is_effective === undefined ? '' : full.is_effective ? 'sim' : 'nao',
        });
        setActionPlan((full.actions || []).map((a) => ({
          key: a.id,
          id: a.id,
          what: a.what || '',
          responsible_id: a.responsible_id || '',
          target_date: a.target_date || '',
          status: a.status || 'pending',
        })));
        await loadAttachments(full.id);
        setShowModal(true);
      } catch (error) {
        toast.error('Erro ao carregar dados do SACPM');
      }
    } else {
      setEditingRecord(null);
      setFormData(emptyForm());
      setActionPlan([]);
      setAttachments([]);
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRecord(null);
  };

  const addActionRow = () => {
    setActionPlan((prev) => [
      ...prev,
      { key: `new-${Date.now()}`, id: null, what: '', responsible_id: '', target_date: '', status: 'pending' },
    ]);
  };

  const updateActionRow = (key, field, value) => {
    setActionPlan((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
  };

  const removeActionRow = (key) => {
    setActionPlan((prev) => prev.filter((r) => r.key !== key));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const sacpmId = editingRecord?.id;
    if (!sacpmId) {
      toast.error('Salve o SACPM antes de anexar arquivos.');
      e.target.value = '';
      return;
    }
    try {
      for (const file of files) {
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        const kind = IMAGE_EXTENSIONS.includes(ext) ? 'image' : 'file';
        const res = await sacpmApi.uploadAttachment(sacpmId, file, kind);
        setAttachments((prev) => [...prev, res.data]);
      }
      toast.success('Arquivo(s) anexado(s)!');
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao anexar arquivo'));
    }
    e.target.value = '';
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const res = await sacpmApi.downloadAttachment(attachment.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar arquivo');
    }
  };

  const handleDeleteAttachment = async (attachment) => {
    try {
      await sacpmApi.deleteAttachment(attachment.id);
      setAttachments((prev) => prev.filter((a) => a.id !== attachment.id));
      toast.success('Anexo removido');
    } catch (error) {
      toast.error('Erro ao remover anexo');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.description) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }

    const dateError = validateDateFields([
      { value: formData.target_date, label: 'Data Alvo' },
    ]);
    if (dateError) { toast.error(dateError); return; }

    setSaving(true);

    try {
      const data = {
        ...formData,
        area: formData.area || null,
        process: formData.process || null,
        responsible_id: formData.responsible_id || null,
        target_date: formData.target_date || null,
        detection_location: formData.detection_location || null,
        external_reference: formData.external_reference || null,
        immediate_action: formData.immediate_action || null,
        immediate_action_description: formData.immediate_action_description || null,
        root_cause: formData.root_cause || null,
        root_cause_method: formData.root_cause_method || null,
        extends_to_other_products: formData.extends_to_other_products === '' ? null : formData.extends_to_other_products === 'sim',
        extends_target_products_processes: formData.extends_target_products_processes || null,
        verification_criteria: formData.verification_criteria || null,
        verification_result: formData.verification_result || null,
        is_effective: formData.is_effective === '' ? null : formData.is_effective === 'sim',
      };

      let sacpmId;
      if (editingRecord) {
        await sacpmApi.update(editingRecord.id, data);
        sacpmId = editingRecord.id;
        toast.success('SACPM atualizado!');
      } else {
        const res = await sacpmApi.create(data);
        sacpmId = res.data.id;
        toast.success('SACPM registrado!');
      }

      await saveActionPlan(sacpmId);

      handleCloseModal();
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao salvar'));
    } finally {
      setSaving(false);
    }
  };

  const saveActionPlan = async (sacpmId) => {
    const existingIds = (editingRecord?.actions || []).map((a) => a.id);
    const keepIds = actionPlan.filter((r) => r.id).map((r) => r.id);

    const toDelete = existingIds.filter((id) => !keepIds.includes(id));
    for (const id of toDelete) {
      try { await sacpmApi.deleteAction(id); } catch (_) { /* ignore */ }
    }

    for (const row of actionPlan) {
      const payload = {
        what: row.what,
        responsible_id: row.responsible_id || null,
        target_date: row.target_date || null,
        status: row.status || 'pending',
      };
      if (!payload.what) continue;
      if (row.id) {
        try { await sacpmApi.updateAction(row.id, payload); } catch (_) { /* ignore */ }
      } else {
        try { await sacpmApi.createAction(sacpmId, payload); } catch (_) { /* ignore */ }
      }
    }
  };

  const handleDelete = async (record) => {
    if (!window.confirm(`Excluir "${record.code}"?`)) return;
    try {
      await sacpmApi.delete(record.id);
      toast.success('SACPM excluído!');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleViewDetails = async (record) => {
    try {
      const res = await sacpmApi.get(record.id);
      setSelectedRecord(res.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handlePrintPdf = async (record) => {
    setPrinting(true);
    try {
      const res = await sacpmApi.getPdf(record.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Erro ao gerar PDF');
    } finally {
      setPrinting(false);
    }
  };

  const handleOpenClose = (record) => {
    setSelectedRecord(record);
    setClosePhoto(null);
    setCloseEffectiveness('effective');
    setCloseVerificationResult('');
    setShowCloseModal(true);
  };

  const handleCloseRequest = async (userName) => {
    if (!selectedRecord) return;
    if (!closePhoto) {
      toast.error('Adicione uma foto de fechamento');
      return;
    }
    setClosing(true);
    try {
      await sacpmApi.close(selectedRecord.id, {
        photo: closePhoto,
        effectiveness: closeEffectiveness,
        verification_result: closeVerificationResult,
      });
      if (closeEffectiveness === 'effective') {
        toast.success(`SACPM encerrado e assinado por ${userName}`);
      } else {
        toast.error('Ação não eficaz: o SACPM não foi encerrado e permanece aberto para reavaliação.');
      }
      setShowCloseModal(false);
      setShowDetailModal(false);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao encerrar'));
    } finally {
      setClosing(false);
    }
  };

  const filteredRecords = records.filter(r =>
    r.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.area?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.open;
    const Icon = config.icon;
    return (
      <span className={`badge ${config.color}`}>
        <Icon size={12} className="mr-1" />
        {config.label}
      </span>
    );
  };

  const TypeBadge = ({ type }) => {
    const config = SACPM_TYPES.find(t => t.value === type) || SACPM_TYPES[0];
    const Icon = config.icon;
    return (
      <span className={`badge ${config.color}`}>
        <Icon size={12} className="mr-1" />
        {config.label}
      </span>
    );
  };

  const PriorityBadge = ({ priority }) => {
    const config = PRIORITIES.find(p => p.value === priority) || PRIORITIES[1];
    return <span className={`badge ${config.color}`}>{config.label}</span>;
  };

  const SectionTitle = ({ children, num }) => (
    <div className="flex items-center gap-2 border-b border-gray-700 pb-2 mb-4">
      <span className="bg-green-500/20 text-green-400 rounded px-2 py-0.5 text-xs font-bold">{num}</span>
      <h3 className="text-sm font-semibold text-white">{children}</h3>
    </div>
  );

  const inputCls = 'input w-full';
  const fieldLabel = 'block text-sm font-medium text-gray-300 mb-2';

  const COLUMNS = [
    { key: 'code', label: 'Código', locked: true, render: (record) => <td className="font-mono text-green-400">{record.code}</td> },
    { key: 'title', label: 'Título', render: (record) => <td><div className="max-w-xs truncate">{record.title}</div></td> },
    { key: 'type', label: 'Tipo', render: (record) => <td><TypeBadge type={record.sacpm_type} /></td> },
    { key: 'priority', label: 'Prioridade', render: (record) => <td><PriorityBadge priority={record.priority} /></td> },
    { key: 'status', label: 'Status', render: (record) => <td><StatusBadge status={record.status} /></td> },
    { key: 'requester', label: 'Emitente', render: (record) => <td>{record.requester_name || '-'}</td> },
    { key: 'area', label: 'Área', render: (record) => <td>{record.area || '-'}</td> },
    { key: 'target', label: 'Prazo', render: (record) => <td>{record.target_date ? toISODate(record.target_date) : '-'}</td> },
    { key: 'actions', label: 'Ações', locked: true, headerClass: 'text-right', render: (record) => {
      const editable = canEditRecord(record);
      return (
        <td className="text-right">
          <div className="flex justify-end gap-2">
            <button onClick={() => handleViewDetails(record)} className="p-1 hover:text-blue-400 transition-colors" title="Ver detalhes">
              <Eye size={16} />
            </button>
            <button onClick={() => handlePrintPdf(record)} disabled={printing} className="p-1 hover:text-purple-400 transition-colors" title="Imprimir PDF">
              <Printer size={16} />
            </button>
            {editable && (
              <>
                <button onClick={() => handleOpenModal(record)} className="p-1 hover:text-green-400 transition-colors" title="Editar">
                  <Edit size={16} />
                </button>
                <button onClick={() => handleDelete(record)} className="p-1 hover:text-red-400 transition-colors" title="Excluir">
                  <Trash2 size={16} />
                </button>
              </>
            )}
          </div>
        </td>
      );
    } },
  ];

  const { visibleColumns, visibleKeys, toggleColumn, resetColumns } = useColumnVisibility({ storageKey: 'sacpm', columns: COLUMNS });

  return (
    <div className="space-y-6" data-testid="sacpm-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">SACPM</h1>
          <p className="text-gray-400 text-sm">Solicitação de Ação Corretiva/Preventiva/Melhoria</p>
        </div>
        <button onClick={() => handleOpenModal()} className="btn-primary" data-testid="new-sacpm-btn">
          <Plus size={20} />
          Novo SACPM
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Total</p>
            <p className="text-2xl font-bold text-white">{stats.total}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Abertas</p>
            <p className="text-2xl font-bold text-yellow-500">{stats.open}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Corretivas</p>
            <p className="text-2xl font-bold text-red-500">{stats.by_type?.corrective || 0}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Preventivas</p>
            <p className="text-2xl font-bold text-blue-500">{stats.by_type?.preventive || 0}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Melhorias</p>
            <p className="text-2xl font-bold text-green-500">{stats.by_type?.improvement || 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Buscar por código, título ou área..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10 w-full"
              data-testid="search-input"
            />
          </div>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="input px-3 w-full sm:w-60" data-testid="type-filter">
            <option value="">Todos os Tipos</option>
            {SACPM_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="input px-3 w-full sm:w-60" data-testid="status-filter">
            <option value="">Todos os Status</option>
            {Object.entries(STATUS_CONFIG).map(([value, config]) => (
              <option key={value} value={value}>{config.label}</option>
            ))}
          </select>
          <button onClick={loadData} className="btn-secondary" data-testid="refresh-btn">
            <RefreshCw size={20} />
          </button>
          <div className="flex items-center">
            <ColumnSelector columns={COLUMNS} visibleKeys={visibleKeys} onToggle={toggleColumn} onReset={resetColumns} />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : filteredRecords.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <p>Nenhum SACPM encontrado</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  {visibleColumns.map(col => (
                    <th key={col.key} className={col.headerClass || ''}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map((record) => (
                  <tr key={record.id} data-testid={`sacpm-row-${record.id}`}>
                    {visibleColumns.map(col => (
                      <Fragment key={col.key}>{col.render(record)}</Fragment>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ===== Create/Edit Modal ===== */}
      {showModal && (
        <>
          <div className="modal-backdrop" onClick={handleCloseModal} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-4xl max-h-[92vh] overflow-y-auto p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white" data-testid="sacpm-modal-title">
                  {editingRecord ? `Editar ${editingRecord.code}` : 'Novo SACPM'}
                </h2>
                <button onClick={handleCloseModal} className="text-gray-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-8">
                {/* 1. Identificação */}
                <div>
                  <SectionTitle num="1">Identificação</SectionTitle>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className={fieldLabel}>Tipo *</label>
                      <select value={formData.sacpm_type} onChange={(e) => setFormData({ ...formData, sacpm_type: e.target.value })} className={inputCls}>
                        {SACPM_TYPES.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={fieldLabel}>Origem</label>
                      <select value={formData.origin} onChange={(e) => setFormData({ ...formData, origin: e.target.value })} className={inputCls}>
                        {ORIGINS.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={fieldLabel}>Prioridade</label>
                      <select value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value })} className={inputCls}>
                        {PRIORITIES.map(p => (
                          <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className={fieldLabel}>Título *</label>
                    <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className={inputCls} placeholder="Título da solicitação" data-testid="sacpm-title-input" />
                  </div>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={fieldLabel}>Setor</label>
                      <select value={formData.area} onChange={(e) => setFormData({ ...formData, area: e.target.value })} className={inputCls}>
                        <option value="">Selecione um setor</option>
                        {sectors.map(s => (
                          <option key={s.id} value={s.name}>{s.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={fieldLabel}>Processo</label>
                      <select value={formData.process} onChange={(e) => setFormData({ ...formData, process: e.target.value })} className={inputCls}>
                        <option value="">Selecione um processo</option>
                        {processes.map(p => (
                          <option key={p.id} value={p.name}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={fieldLabel}>Nº SAC fornecedor / cliente</label>
                      <input type="text" value={formData.external_reference} onChange={(e) => setFormData({ ...formData, external_reference: e.target.value })} className={inputCls} placeholder="Ex: SAC-2025-001 / CL-01123" />
                    </div>
                    <div>
                      <label className={fieldLabel}>Local de detecção</label>
                      <input type="text" value={formData.detection_location} onChange={(e) => setFormData({ ...formData, detection_location: e.target.value })} className={inputCls} placeholder="Ex: Linha 1, Armazém, Inspeção final" />
                    </div>
                    <div>
                      <label className={fieldLabel}>Responsável</label>
                      <select value={formData.responsible_id} onChange={(e) => setFormData({ ...formData, responsible_id: e.target.value })} className={inputCls}>
                        <option value="">Selecione...</option>
                        {users.map(u => (
                          <option key={u.id} value={u.id}>{u.full_name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={fieldLabel}>Data Alvo</label>
                      <input type="date" value={formData.target_date} onChange={(e) => setFormData({ ...formData, target_date: e.target.value })} className={inputCls} min="1900-01-01" max="2099-12-31" />
                    </div>
                  </div>
                </div>

                {/* 2. Descrição */}
                <div>
                  <SectionTitle num="2">Descrição</SectionTitle>
                  <div>
                    <label className={fieldLabel}>Descrição *</label>
                    <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className={inputCls} rows={3} placeholder="Descreva a solicitação" data-testid="sacpm-description-input" />
                  </div>
                  <div className="mt-4">
                    <label className={fieldLabel}>Detalhes do problema</label>
                    <textarea value={formData.problem_description} onChange={(e) => setFormData({ ...formData, problem_description: e.target.value })} className={inputCls} rows={3} placeholder="Descreva o problema identificado" />
                  </div>
                </div>

                {/* 3. Anexos */}
                <div>
                  <SectionTitle num="3">Anexos (imagens / arquivos)</SectionTitle>
                  <label className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-600 rounded-lg p-6 cursor-pointer hover:border-green-500 transition-colors">
                    <Upload size={20} className="text-green-500" />
                    <span className="text-sm text-gray-300">Clique para anexar imagens ou arquivos</span>
                    <input type="file" multiple onChange={handleFileUpload} className="hidden" />
                  </label>
                  {attachments.length > 0 && (
                    <ul className="mt-3 space-y-2">
                      {attachments.map((a) => (
                        <li key={a.id} className="flex items-center justify-between border border-gray-700 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2 text-sm text-gray-300 truncate">
                            <FileText size={16} className="text-blue-400 shrink-0" />
                            <span className="truncate">{a.file_name}</span>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <button type="button" onClick={() => handleDownloadAttachment(a)} className="text-blue-400 hover:text-blue-300" title="Baixar">
                              <Download size={16} />
                            </button>
                            <button type="button" onClick={() => handleDeleteAttachment(a)} className="text-red-400 hover:text-red-300" title="Remover">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  {!editingRecord && (
                    <p className="text-xs text-gray-500 mt-2">Salve o SACPM para poder anexar imagens e arquivos.</p>
                  )}
                </div>

                {/* 4. Ação imediata */}
                <div>
                  <SectionTitle num="4">Ação Imediata</SectionTitle>
                  <div>
                    <label className={fieldLabel}>Ação imediata a ser feita</label>
                    <input type="text" value={formData.immediate_action} onChange={(e) => setFormData({ ...formData, immediate_action: e.target.value })} className={inputCls} placeholder="Ex: Ação de contenção imediata" />
                  </div>
                  <div className="mt-4">
                    <label className={fieldLabel}>Descrição da ação</label>
                    <textarea value={formData.immediate_action_description} onChange={(e) => setFormData({ ...formData, immediate_action_description: e.target.value })} className={inputCls} rows={3} placeholder="Detalhe a ação imediata" />
                  </div>
                </div>

                {/* 5. Análise de Causa */}
                <div>
                  <SectionTitle num="5">Análise de Causa</SectionTitle>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={fieldLabel}>Método</label>
                      <select value={formData.root_cause_method} onChange={(e) => setFormData({ ...formData, root_cause_method: e.target.value })} className={inputCls}>
                        <option value="">Selecione</option>
                        {ROOT_CAUSE_METHODS.map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className={fieldLabel}>Causa raiz</label>
                    <textarea value={formData.root_cause} onChange={(e) => setFormData({ ...formData, root_cause: e.target.value })} className={inputCls} rows={3} placeholder="Descreva a causa raiz identificada" />
                  </div>
                </div>

                {/* 6. Plano de Ação */}
                <div>
                  <div className="flex items-center justify-between">
                    <SectionTitle num="6">Plano de Ação</SectionTitle>
                    <button type="button" onClick={addActionRow} className="btn-secondary text-sm">
                      <PlusCircle size={16} />
                      Adicionar ação
                    </button>
                  </div>
                  {actionPlan.length === 0 ? (
                    <p className="text-gray-500 text-sm">Nenhuma ação cadastrada.</p>
                  ) : (
                    <div className="space-y-3">
                      {actionPlan.map((row, idx) => (
                        <div key={row.key} className="border border-gray-700 rounded-lg p-3 space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-gray-400">Ação #{idx + 1}</span>
                            <button type="button" onClick={() => removeActionRow(row.key)} className="text-red-400 hover:text-red-300" title="Remover">
                              <Minus size={16} />
                            </button>
                          </div>
                          <div>
                            <label className={fieldLabel}>Ação a ser tomada</label>
                            <input type="text" value={row.what} onChange={(e) => updateActionRow(row.key, 'what', e.target.value)} className={inputCls} placeholder="Descreva a ação" />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className={fieldLabel}>Responsável</label>
                              <select value={row.responsible_id} onChange={(e) => updateActionRow(row.key, 'responsible_id', e.target.value)} className={inputCls}>
                                <option value="">Selecione...</option>
                                {users.map(u => (
                                  <option key={u.id} value={u.id}>{u.full_name}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className={fieldLabel}>Prazo</label>
                              <input type="date" value={row.target_date} onChange={(e) => updateActionRow(row.key, 'target_date', e.target.value)} className={inputCls} min="1900-01-01" max="2099-12-31" />
                            </div>
                            <div>
                              <label className={fieldLabel}>Status</label>
                              <select value={row.status} onChange={(e) => updateActionRow(row.key, 'status', e.target.value)} className={inputCls}>
                                {ACTION_STATUS_OPTIONS.map(o => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* 7. Extensividade */}
                <div>
                  <SectionTitle num="7">Extensão a outros produtos / processos</SectionTitle>
                  <div className="mb-4">
                    <label className={fieldLabel}>As ações corretivas/preventivas são extensivas a outros produtos ou processos?</label>
                    <div className="flex gap-4">
                      {['sim', 'nao'].map(opt => (
                        <label key={opt} className="flex items-center gap-2 text-white cursor-pointer">
                          <input
                            type="radio"
                            name="extends_to_other_products"
                            checked={formData.extends_to_other_products === opt}
                            onChange={() => setFormData({ ...formData, extends_to_other_products: opt })}
                          />
                          {opt === 'sim' ? 'Sim' : 'Não'}
                        </label>
                      ))}
                    </div>
                  </div>
                  {formData.extends_to_other_products === 'sim' && (
                    <div>
                      <label className={fieldLabel}>Quais produtos / processos?</label>
                      <textarea
                        value={formData.extends_target_products_processes}
                        onChange={(e) => setFormData({ ...formData, extends_target_products_processes: e.target.value })}
                        className={inputCls}
                        rows={2}
                        placeholder="Defina os produtos ou processos extensivos. Se não houver, pode informar N/A."
                      />
                    </div>
                  )}
                </div>

                {/* 8. Validação da eficácia */}
                <div>
                  <SectionTitle num="8">Validação da Eficácia</SectionTitle>
                  <div>
                    <label className={fieldLabel}>Critérios de verificação</label>
                    <textarea value={formData.verification_criteria} onChange={(e) => setFormData({ ...formData, verification_criteria: e.target.value })} className={inputCls} rows={2} placeholder="Defina como a eficácia será verificada" />
                  </div>
                  <div className="mt-4">
                    <label className={fieldLabel}>Resultado da verificação</label>
                    <textarea value={formData.verification_result} onChange={(e) => setFormData({ ...formData, verification_result: e.target.value })} className={inputCls} rows={2} placeholder="Descreva o resultado" />
                  </div>
                  <div className="mt-4">
                    <label className={fieldLabel}>A ação foi eficaz?</label>
                    <div className="flex gap-4">
                      {['sim', 'nao'].map(opt => (
                        <label key={opt} className="flex items-center gap-2 text-white cursor-pointer">
                          <input
                            type="radio"
                            name="is_effective"
                            checked={formData.is_effective === opt}
                            onChange={() => setFormData({ ...formData, is_effective: opt })}
                          />
                          {opt === 'sim' ? 'Sim' : 'Não'}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                  <button type="button" onClick={handleCloseModal} className="btn-secondary">Cancelar</button>
                  <button type="submit" className="btn-primary" disabled={saving} data-testid="sacpm-submit-btn">
                    {saving ? <Loader2 className="animate-spin" size={20} /> : editingRecord ? 'Salvar' : 'Registrar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* ===== Detail Modal ===== */}
      {showDetailModal && selectedRecord && (
        <>
          <div className="modal-backdrop" onClick={() => setShowDetailModal(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-4xl max-h-[92vh] overflow-y-auto p-6 shadow-2xl">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-white">{selectedRecord.code}</h2>
                  <p className="text-gray-400">{selectedRecord.title}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handlePrintPdf(selectedRecord)} disabled={printing} className="btn-secondary text-sm">
                    {printing ? <Loader2 className="animate-spin" size={16} /> : <Printer size={16} />}
                    Imprimir PDF
                  </button>
                  <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-white">
                    <X size={24} />
                  </button>
                </div>
              </div>

              <div className="flex gap-2 flex-wrap mb-4">
                <TypeBadge type={selectedRecord.sacpm_type} />
                <StatusBadge status={selectedRecord.status} />
                <PriorityBadge priority={selectedRecord.priority} />
              </div>

              {/* Identificação */}
              <div className="border border-gray-700 rounded-lg p-4 mb-4">
                <p className="text-gray-400 text-xs font-semibold mb-2 uppercase">Identificação</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div><p className="text-gray-400">Emitente</p><p className="text-white">{selectedRecord.requester_name || '-'}</p></div>
                  <div><p className="text-gray-400">Setor</p><p className="text-white">{selectedRecord.area || '-'}</p></div>
                  <div><p className="text-gray-400">Processo</p><p className="text-white">{selectedRecord.process || '-'}</p></div>
                  <div><p className="text-gray-400">Data de emissão</p><p className="text-white">{selectedRecord.opening_date ? toISODate(selectedRecord.opening_date) : '-'}</p></div>
                  <div><p className="text-gray-400">Nº SAC fornecedor/cliente</p><p className="text-white">{selectedRecord.external_reference || '-'}</p></div>
                  <div><p className="text-gray-400">Local de detecção</p><p className="text-white">{selectedRecord.detection_location || '-'}</p></div>
                  <div><p className="text-gray-400">Responsável</p><p className="text-white">{selectedRecord.responsible_name || '-'}</p></div>
                  <div><p className="text-gray-400">Data Alvo</p><p className="text-white">{selectedRecord.target_date ? toISODate(selectedRecord.target_date) : '-'}</p></div>
                </div>
              </div>

              {/* Descrição */}
              <div className="border border-gray-700 rounded-lg p-4 mb-4">
                <p className="text-gray-400 text-xs font-semibold mb-2 uppercase">Descrição</p>
                <p className="text-white whitespace-pre-wrap">{selectedRecord.description}</p>
                {selectedRecord.problem_description && (
                  <>
                    <p className="text-gray-400 text-sm mt-3">Detalhes do problema</p>
                    <p className="text-white whitespace-pre-wrap">{selectedRecord.problem_description}</p>
                  </>
                )}
              </div>

              {/* Ação imediata */}
              {(selectedRecord.immediate_action || selectedRecord.immediate_action_description) && (
                <div className="border border-gray-700 rounded-lg p-4 mb-4">
                  <p className="text-gray-400 text-xs font-semibold mb-2 uppercase">Ação Imediata</p>
                  <p className="text-white whitespace-pre-wrap">{selectedRecord.immediate_action || '-'}</p>
                  <p className="text-gray-400 text-sm mt-3">Descrição</p>
                  <p className="text-white whitespace-pre-wrap">{selectedRecord.immediate_action_description || '-'}</p>
                </div>
              )}

              {/* Análise de causa */}
              {(selectedRecord.root_cause || selectedRecord.root_cause_method) && (
                <div className="border border-gray-700 rounded-lg p-4 mb-4">
                  <p className="text-gray-400 text-xs font-semibold mb-2 uppercase">Análise de Causa</p>
                  <p className="text-gray-400 text-sm">Método</p>
                  <p className="text-white">{selectedRecord.root_cause_method || '-'}</p>
                  <p className="text-gray-400 text-sm mt-3">Causa raiz</p>
                  <p className="text-white whitespace-pre-wrap">{selectedRecord.root_cause || '-'}</p>
                </div>
              )}

              {/* Plano de ação */}
              <div className="border border-gray-700 rounded-lg p-4 mb-4">
                <p className="text-gray-400 text-xs font-semibold mb-2 uppercase">Plano de Ação</p>
                {selectedRecord.actions && selectedRecord.actions.length > 0 ? (
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th className="w-12">Nº</th>
                          <th>Ação</th>
                          <th>Responsável</th>
                          <th>Prazo</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedRecord.actions.map((a) => (
                          <tr key={a.id}>
                            <td>{a.sequence}</td>
                            <td className="whitespace-pre-wrap">{a.what}</td>
                            <td>{a.responsible_name || '-'}</td>
                            <td>{a.target_date ? toISODate(a.target_date) : '-'}</td>
                            <td>{a.status || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Nenhuma ação cadastrada.</p>
                )}
              </div>

              {/* Extensividade */}
              {selectedRecord.extends_to_other_products !== null && selectedRecord.extends_to_other_products !== undefined && (
                <div className="border border-gray-700 rounded-lg p-4 mb-4">
                  <p className="text-gray-400 text-xs font-semibold mb-2 uppercase">Extensão a outros produtos/processos</p>
                  <p className="text-white">{selectedRecord.extends_to_other_products ? 'Sim' : 'Não'}</p>
                  {selectedRecord.extends_to_other_products && selectedRecord.extends_target_products_processes && (
                    <p className="text-white whitespace-pre-wrap mt-2">{selectedRecord.extends_target_products_processes}</p>
                  )}
                </div>
              )}

              {/* Verificação de eficácia */}
              {(selectedRecord.verification_result || selectedRecord.verification_criteria || selectedRecord.is_effective !== null) && (
                <div className="border border-gray-700 rounded-lg p-4 mb-4">
                  <p className="text-gray-400 text-xs font-semibold mb-2 uppercase">Verificação de Eficácia</p>
                  {selectedRecord.verification_criteria && (
                    <>
                      <p className="text-gray-400 text-sm">Critérios</p>
                      <p className="text-white whitespace-pre-wrap">{selectedRecord.verification_criteria}</p>
                    </>
                  )}
                  <p className="text-gray-400 text-sm mt-3">Resultado</p>
                  <p className="text-white whitespace-pre-wrap">{selectedRecord.verification_result || '-'}</p>
                  {selectedRecord.is_effective !== null && (
                    <p className="text-sm mt-2">
                      <span className={selectedRecord.is_effective ? 'text-green-500' : 'text-red-500'}>
                        {selectedRecord.is_effective ? '✓ Eficaz' : '✗ Não Eficaz'}
                      </span>
                    </p>
                  )}
                </div>
              )}

              {/* Anexos */}
              <div className="border border-gray-700 rounded-lg p-4 mb-4">
                <p className="text-gray-400 text-xs font-semibold mb-2 uppercase">Anexos ({selectedRecord.attachments?.length || 0})</p>
                {selectedRecord.attachments && selectedRecord.attachments.length > 0 ? (
                  <ul className="space-y-2">
                    {selectedRecord.attachments.map((a) => (
                      <li key={a.id} className="flex items-center justify-between text-sm text-gray-300">
                        <span className="truncate flex items-center gap-2">
                          <FileText size={16} className="text-blue-400" />
                          {a.file_name}
                        </span>
                        <button onClick={() => handleDownloadAttachment(a)} className="text-blue-400 hover:text-blue-300">
                          <Download size={16} />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 text-sm">Nenhum anexo.</p>
                )}
              </div>

              {/* Assinatura */}
              {(selectedRecord.signature_name || selectedRecord.closing_photo_path) && (
                <div className="border border-gray-700 rounded-lg p-4 mb-4">
                  <p className="text-gray-400 text-xs font-semibold mb-2 uppercase">Encerramento</p>
                  <p className="text-white">
                    Assinado por <span className="font-semibold">{selectedRecord.signature_name}</span>
                    {selectedRecord.signature_date ? ` em ${toISODate(selectedRecord.signature_date)}` : ''}
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button onClick={() => setShowDetailModal(false)} className="btn-secondary">Fechar</button>
                {canEditRecord(selectedRecord) && (
                  <>
                    {selectedRecord.status !== 'closed_effective' && selectedRecord.status !== 'closed_ineffective' && (
                      <button onClick={() => handleOpenClose(selectedRecord)} className="btn-primary">
                        <CheckCircle size={16} />
                        Encerrar
                      </button>
                    )}
                    <button
                      onClick={() => { setShowDetailModal(false); handleOpenModal(selectedRecord); }}
                      className="btn-secondary"
                    >
                      <Edit size={16} />
                      Editar
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ===== Close Modal ===== */}
      {showCloseModal && selectedRecord && (
        <>
          <div className="modal-backdrop" onClick={() => setShowCloseModal(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-lg p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Encerrar {selectedRecord.code}</h2>
                <button onClick={() => setShowCloseModal(false)} className="text-gray-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className={fieldLabel}>Validação da eficácia *</label>
                  <div className="flex gap-4">
                    {['effective', 'ineffective'].map(opt => (
                      <label key={opt} className="flex items-center gap-2 text-white cursor-pointer">
                        <input
                          type="radio"
                          name="closeEffectiveness"
                          checked={closeEffectiveness === opt}
                          onChange={() => setCloseEffectiveness(opt)}
                        />
                        {opt === 'effective' ? 'Eficaz' : 'Não Eficaz'}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className={fieldLabel}>Resultado da verificação</label>
                  <textarea
                    value={closeVerificationResult}
                    onChange={(e) => setCloseVerificationResult(e.target.value)}
                    className={inputCls}
                    rows={3}
                    placeholder="Descreva o resultado da verificação da eficácia"
                  />
                </div>

                <div>
                  <label className={fieldLabel}>Foto / evidência de fechamento *</label>
                  {closePhoto ? (
                    <div className="flex items-center justify-between border border-gray-700 rounded-lg px-3 py-2">
                      <span className="text-sm text-gray-300 truncate">{closePhoto.name}</span>
                      <button type="button" onClick={() => setClosePhoto(null)} className="text-red-400 hover:text-red-300">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-600 rounded-lg p-6 cursor-pointer hover:border-green-500 transition-colors">
                      <Upload size={20} className="text-green-500" />
                      <span className="text-sm text-gray-300">Selecione uma foto</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => setClosePhoto(e.target.files?.[0] || null)}
                      />
                    </label>
                  )}
                </div>

                {closeEffectiveness === 'ineffective' ? (
                  <div className="bg-red-500/10 border border-red-500/20 rounded p-3 text-sm text-red-300">
                    Uma ação não eficaz não pode ser encerrada. O SACPM permanecerá aberto,
                    com status "Reaberta - Não Eficaz", para nova análise e plano de ação.
                  </div>
                ) : (
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded p-3 text-sm text-blue-300">
                    Ao confirmar, você deverá validar sua identidade por assinatura eletrônica.
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                  <button type="button" onClick={() => setShowCloseModal(false)} className="btn-secondary" disabled={closing}>
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowSignature(true)}
                    className="btn-primary"
                    disabled={closing || !closePhoto}
                  >
                    <CheckCircle size={16} />
                    Continuar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Electronic Signature */}
      <ElectronicSignature
        isOpen={showSignature}
        onClose={() => setShowSignature(false)}
        onConfirm={(userName) => {
          setShowSignature(false);
          handleCloseRequest(userName);
        }}
        title={closeEffectiveness === 'ineffective' ? 'Registrar Verificação' : 'Encerrar SACPM'}
        description={
          closeEffectiveness === 'ineffective'
            ? `Você está registrando que "${selectedRecord?.code}" não foi eficaz. Ele permanecerá aberto. Confirme sua identidade para assinar.`
            : `Você está encerrando "${selectedRecord?.code}". Confirme sua identidade para assinar.`
        }
      />
    </div>
  );
};

export default SACPM;